import { Fragment, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import type {
  AdminListItem,
  PaginatedAuditLog,
  SettingsOverview,
} from '../services/types';
import { formatDate } from '../utils/format';
import { useAuth } from '../hooks/useAuth';

type Tab = 'overview' | 'admins' | 'audit';

const ROLES = ['superadmin', 'admin', 'finance', 'support', 'readonly'];

export function SettingsPage() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) || 'overview';

  const setTab = (t: Tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    if (t !== 'audit') {
      next.delete('page');
      next.delete('q');
      next.delete('action');
    }
    setSearchParams(next);
  };

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Настройки</h1>

      <div className="tabs">
        <button
          type="button"
          className={`tab ${tab === 'overview' ? 'active' : ''}`}
          onClick={() => setTab('overview')}
        >
          Обзор
        </button>
        {isSuperadmin && (
          <button
            type="button"
            className={`tab ${tab === 'admins' ? 'active' : ''}`}
            onClick={() => setTab('admins')}
          >
            Админы
          </button>
        )}
        <button
          type="button"
          className={`tab ${tab === 'audit' ? 'active' : ''}`}
          onClick={() => setTab('audit')}
        >
          Audit log
        </button>
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'admins' && isSuperadmin && <AdminsTab />}
      {tab === 'audit' && <AuditTab searchParams={searchParams} setSearchParams={setSearchParams} />}
    </div>
  );
}

function OverviewTab() {
  const [data, setData] = useState<SettingsOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await api.getSettingsOverview();
      if (res.data) setData(res.data);
      setLoading(false);
    })();
  }, []);

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>;
  if (!data) return <p style={{ color: 'var(--danger)' }}>Не удалось загрузить настройки</p>;

  return (
    <div className="grid-stats">
      <InfoCard label="API version">{data.api_version}</InfoCard>
      <InfoCard label="Environment">{data.environment}</InfoCard>
      <InfoCard label="Database">
        <span className={data.database === 'ok' ? 'status-ok' : 'status-error'}>{data.database}</span>
      </InfoCard>
      <InfoCard label="Redis">
        <span className={data.redis === 'ok' ? 'status-ok' : 'status-error'}>{data.redis}</span>
      </InfoCard>
      <InfoCard label="Bot username">{data.bot_username_configured ? 'настроен' : 'не задан'}</InfoCard>
      <InfoCard label="WG nodes">{data.wg_nodes_configured}</InfoCard>
      <InfoCard label="Servers cache">{data.runtime_servers_cached}</InfoCard>
      <InfoCard label="Session TTL">{data.session_ttl_seconds}s</InfoCard>
      <InfoCard label="Rate limit">
        {data.rate_limit_requests} / {data.rate_limit_window_seconds}s
      </InfoCard>
      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="stat-label">URLs</div>
        <div style={{ fontSize: '0.875rem' }}>
          Panel: {data.admin_panel_url}
          <br />
          Portal: {data.user_portal_url}
          <br />
          Base path: {data.app_base_path}
        </div>
      </div>
      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="stat-label">CORS origins</div>
        <div style={{ fontSize: '0.875rem' }}>{data.cors_origins.join(', ') || '—'}</div>
      </div>
      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="stat-label">Panel migrations</div>
        <div style={{ fontSize: '0.875rem' }}>{data.panel_migrations_applied.join(', ') || '—'}</div>
      </div>
    </div>
  );
}

function AdminsTab() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<AdminListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [newId, setNewId] = useState('');
  const [newRole, setNewRole] = useState('admin');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.listAdmins();
    if (res.data) setAdmins(res.data);
    else if (res.error) setError(res.error.message);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    const res = await api.upsertAdmin({
      telegram_user_id: Number(newId),
      role: newRole,
    });
    if (res.data) {
      setMessage(`Админ ${res.data.telegram_user_id} добавлен`);
      setNewId('');
      load();
    } else if (res.error) setError(res.error.message);
  };

  const changeRole = async (admin: AdminListItem, role: string) => {
    if (admin.telegram_user_id === user?.telegram_user_id) return;
    const res = await api.updateAdmin(admin.telegram_user_id, { role });
    if (res.data) load();
    else if (res.error) setError(res.error.message);
  };

  const toggleActive = async (admin: AdminListItem) => {
    if (admin.telegram_user_id === user?.telegram_user_id) return;
    const res = await api.updateAdmin(admin.telegram_user_id, { is_active: !admin.is_active });
    if (res.data) load();
    else if (res.error) setError(res.error.message);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Добавить админа</h3>
        <form onSubmit={addAdmin} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <input
            className="input"
            placeholder="Telegram user ID"
            value={newId}
            onChange={(e) => setNewId(e.target.value)}
            required
            style={{ width: 160 }}
          />
          <select className="select" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button type="submit" className="btn btn-primary">
            Добавить
          </button>
        </form>
      </div>

      {message && <p style={{ color: 'var(--success)' }}>{message}</p>}
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}

      {!loading && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Telegram ID</th>
                <th>Role</th>
                <th>Active</th>
                <th>Last login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => {
                const isSelf = a.telegram_user_id === user?.telegram_user_id;
                return (
                  <tr key={a.id}>
                    <td>{a.telegram_user_id}</td>
                    <td>
                      <select
                        className="select"
                        value={a.role}
                        disabled={isSelf}
                        onChange={(e) => changeRole(a, e.target.value)}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span className={a.is_active ? 'badge badge-success' : 'badge badge-muted'}>
                        {a.is_active ? 'да' : 'нет'}
                      </span>
                    </td>
                    <td>{formatDate(a.last_login_at)}</td>
                    <td>
                      {!isSelf && (
                        <button type="button" className="btn btn-ghost" onClick={() => toggleActive(a)}>
                          {a.is_active ? 'Деактивировать' : 'Активировать'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AuditTab({
  searchParams,
  setSearchParams,
}: {
  searchParams: URLSearchParams;
  setSearchParams: (p: URLSearchParams) => void;
}) {
  const [data, setData] = useState<PaginatedAuditLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const page = Number(searchParams.get('page') || '1');
  const q = searchParams.get('q') || '';
  const action = searchParams.get('action') || '';
  const [qInput, setQInput] = useState(q);
  const [actionInput, setActionInput] = useState(action);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.listAuditLog({
      page,
      page_size: 30,
      q: q || undefined,
      action: action || undefined,
    });
    if (res.data) setData(res.data);
    setLoading(false);
  }, [page, q, action]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'audit');
    if (qInput) next.set('q', qInput);
    else next.delete('q');
    if (actionInput) next.set('action', actionInput);
    else next.delete('action');
    next.set('page', '1');
    setSearchParams(next);
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'audit');
    next.set('page', String(p));
    setSearchParams(next);
  };

  return (
    <div>
      <form onSubmit={applyFilters} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <input
          className="input"
          placeholder="Поиск: action, resource, actor ID…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          style={{ minWidth: 220 }}
        />
        <input
          className="input"
          placeholder="Action (точное)"
          value={actionInput}
          onChange={(e) => setActionInput(e.target.value)}
          style={{ width: 180 }}
        />
        <button type="submit" className="btn btn-primary">
          Найти
        </button>
      </form>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}

      {!loading && data && (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Время</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <Fragment key={row.id}>
                    <tr
                      style={{ cursor: row.before_json || row.after_json ? 'pointer' : undefined }}
                      onClick={() =>
                        setExpandedId(expandedId === row.id ? null : row.id)
                      }
                    >
                      <td>{row.id}</td>
                      <td>{formatDate(row.created_at)}</td>
                      <td>
                        {row.actor_telegram_id}
                        <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>({row.actor_role})</span>
                      </td>
                      <td>
                        <code style={{ fontSize: '0.75rem' }}>{row.action}</code>
                      </td>
                      <td>
                        {row.resource_type ?? '—'}
                        {row.resource_id ? ` / ${row.resource_id}` : ''}
                      </td>
                      <td>{row.ip ?? '—'}</td>
                    </tr>
                    {expandedId === row.id && (row.before_json || row.after_json) && (
                      <tr>
                        <td colSpan={6}>
                          <pre
                            style={{
                              margin: 0,
                              fontSize: '0.75rem',
                              overflow: 'auto',
                              maxHeight: 200,
                              background: 'var(--bg)',
                              padding: '0.5rem',
                              borderRadius: 'var(--radius)',
                            }}
                          >
                            {row.before_json && `before: ${JSON.stringify(row.before_json, null, 2)}\n`}
                            {row.after_json && `after: ${JSON.stringify(row.after_json, null, 2)}`}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {data.pages > 1 && (
            <div className="pagination">
              <button type="button" className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                ←
              </button>
              <span style={{ color: 'var(--text-muted)' }}>
                {page} / {data.pages} ({data.total})
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page >= data.pages}
                onClick={() => setPage(page + 1)}
              >
                →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div>{children}</div>
    </div>
  );
}
